import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch } from 'react-redux';
import Text from '../../../shared/components/Text/Text';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import { setActiveModal, clearPreSaveItemsInView } from '../ducks/calendarActions';
import { pendingNavigationRef } from '../hooks/usePreSaveNavigationGuard';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const IconCircle = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #F5F5F5;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalText = styled('div')`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const WarningText = styled('div')`
  padding: 12px;
  background: #FFF3CD;
  border: 1px solid #FFE69C;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
`;

const InlineAddButtonLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  background: #10cd8c;
  color: white;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  padding: 0 8px;
  height: 24px;
  border-radius: 4px;
  vertical-align: middle;
  margin: 0 4px;
`;

const ButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DiscardTextLink = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 400;
  background: transparent;
  color: #888;
  border: none;
  padding: 8px 12px;
  margin: 0 auto;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: #555;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2C2C2C;
  }
`;

const CloseButton = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: pointer;
`;

const ConfirmDiscardPreSaveModal = ({ closeModal }) => {
  const dispatch = useDispatch();

  const handleDiscard = () => {
    const continuation = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    /** Synchronous close so the continuation can immediately open another modal
     * (e.g. selectCalendarModal) without a race against closeModal's setTimeout.
     */
    dispatch(setActiveModal('', null));
    /** Drop the in-view preSave items now (while the OLD view's visible keys are
     * still current) so any downstream guard re-evaluation sees them gone.
     */
    dispatch(clearPreSaveItemsInView());
    if (typeof continuation === 'function') {
      continuation();
    }
  };

  const handleCancel = () => {
    pendingNavigationRef.current = null;
    closeModal();
  };

  return (
    <ModalContainer>
      <CloseButton onClick={handleCancel}>
        <IcomoonReact
          iconSet={iconSet}
          size={15}
          icon='remove'
          color='grey'
        />
      </CloseButton>
      <IconCircle>
        <IcomoonReact
          iconSet={iconSet}
          size={32}
          icon='warning'
          color='#DB3030'
        />
      </IconCircle>

      <ModalText>
        <Text fontSize='20px' fontWeight={600}>
          Unsaved exercises in view
        </Text>
      </ModalText>

      <WarningText>
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon='warning'
          color='#856404'
          style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
        />
        <Text fontSize='14px' color='#856404'>
          You have unsaved exercises on days currently in view.
          Please save these items before navigating away from the current view.
          Leaving may cause these items to be discarded.
        </Text>
      </WarningText>

      <Text fontSize='14px' color='#555' style={{ marginBottom: '20px' }}>
        Click the
        <InlineAddButtonLabel>ADD</InlineAddButtonLabel>
        button to save items.
      </Text>

      <ButtonsContainer>
        <CancelButton onClick={handleCancel}>
          CANCEL
        </CancelButton>
        <DiscardTextLink onClick={handleDiscard}>
          Ignore and continue
        </DiscardTextLink>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ConfirmDiscardPreSaveModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default ConfirmDiscardPreSaveModal;
